import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    Typography,
    Tab,
    Tabs,
    Box,
    ImageList,
    ImageListItem,
    CircularProgress,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';

const ImageUploadDialog = ({ open, onClose, onImageSelect, currentImage }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [urlInput, setUrlInput] = useState('');
    const [uploadedImages, setUploadedImages] = useState([]); // Initialize as empty array
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Fetch existing images when dialog opens
    useEffect(() => {
        if (open) {
            fetchUploadedImages();
        }
    }, [open]);

    const fetchUploadedImages = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/media/list`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = await response.json();
            if (Array.isArray(data)) {
                setUploadedImages(data);
            } else {
                console.error('Unexpected response format:', data);
                setUploadedImages([]);
            }
        } catch (err) {
            console.error('Error fetching images:', err);
            setError('Failed to load image library');
            setUploadedImages([]);
        }
        setLoading(false);
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/media/upload`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            if (!response.ok) throw new Error('Upload failed');

            const data = await response.json();
            onImageSelect(data.url);
            onClose();
            // Refresh the image library
            fetchUploadedImages();
        } catch (err) {
            setError('Failed to upload image');
            console.error('Upload error:', err);
        }
        setLoading(false);
    };

    const handleUrlSubmit = () => {
        if (urlInput.trim()) {
            onImageSelect(urlInput);
            onClose();
        }
    };

    const getImageUrl = (path) => {
        if (!path) return '/api/placeholder/400/300';

        // If it's already a full URL, return it as is
        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }

        // Clean the path by removing any leading slashes
        const cleanPath = path.replace(/^\/+/, '');

        // If the path already includes 'uploads/', don't add it again
        if (cleanPath.startsWith('uploads/')) {
            return `${process.env.REACT_APP_API_URL}/${cleanPath}`;
        }

        // Otherwise, ensure uploads/ is in the path
        return `${process.env.REACT_APP_API_URL}/uploads/${cleanPath}`;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Image Upload</DialogTitle>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} centered>
                <Tab icon={<CloudUploadIcon />} label="Upload" />
                <Tab icon={<PhotoLibraryIcon />} label="Library" />
                <Tab icon={<LinkIcon />} label="URL" />
            </Tabs>

            <DialogContent>
                {error && (
                    <Typography color="error" gutterBottom>
                        {error}
                    </Typography>
                )}

                <Box sx={{ mt: 2 }}>
                    {activeTab === 0 && (
                        <Grid container direction="column" spacing={2} alignItems="center">
                            <Grid item>
                                <input
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    id="upload-image-file"
                                    type="file"
                                    onChange={handleFileUpload}
                                />
                                <label htmlFor="upload-image-file">
                                    <Button variant="contained" component="span" startIcon={<ImageIcon />} disabled={loading}>
                                        Choose File
                                    </Button>
                                </label>
                            </Grid>
                            {currentImage && (
                                <Grid item>
                                    <Typography variant="subtitle2">Current Image:</Typography>
                                    <img src={getImageUrl(currentImage)} alt="Current" style={{ maxWidth: '200px', maxHeight: '200px' }} />
                                </Grid>
                            )}
                        </Grid>
                    )}

                    {activeTab === 1 && (
                        <Box sx={{ height: 400, overflowY: 'auto' }}>
                            {loading ? (
                                <CircularProgress />
                            ) : (
                                <ImageList cols={3} gap={8}>
                                    {uploadedImages.map((img, index) => (
                                        <ImageListItem
                                            key={index}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:hover': { opacity: 0.8 },
                                            }}
                                            onClick={() => {
                                                onImageSelect(img.url);
                                                onClose();
                                            }}
                                        >
                                            <img
                                                src={getImageUrl(img.url)}
                                                alt={`Library ${index}`}
                                                loading="lazy"
                                                style={{ height: '120px', objectFit: 'cover' }}
                                            />
                                        </ImageListItem>
                                    ))}
                                </ImageList>
                            )}
                        </Box>
                    )}

                    {activeTab === 2 && (
                        <TextField
                            fullWidth
                            label="Image URL"
                            value={urlInput}
                            onChange={(e) => setUrlInput(e.target.value)}
                            margin="normal"
                        />
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
                {activeTab === 2 && (
                    <Button onClick={handleUrlSubmit} color="primary" disabled={!urlInput.trim()}>
                        Add URL
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ImageUploadDialog;
